import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Calendar, Video } from "lucide-react";
import api from "@/lib/api";

function formatSlot(iso) {
  if (!iso) return "";
  const d = new Date(iso.includes("Z") ? iso : iso + ":00");
  return d.toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function BookingConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | error
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 6) {
        setState("error");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="page-booking-confirmation" className="mx-auto max-w-2xl px-6 lg:px-12 py-16 lg:py-24">
      <img src="/brand/logo-mark.png" alt="Hola Movers" className="mx-auto mb-10 h-14 w-auto" data-testid="confirmation-logo" />
      {state === "checking" && (
        <div className="text-center" data-testid="confirm-checking">
          <Loader2 className="mx-auto animate-spin text-sage" size={44} />
          <h1 className="mt-6 font-serif text-2xl text-charcoal-800">Confirming your payment…</h1>
          <p className="mt-2 text-charcoal-600">Just a moment while we finalize your booking.</p>
        </div>
      )}

      {state === "paid" && (
        <div className="text-center animate-fade-up" data-testid="confirm-success">
          <CheckCircle2 className="mx-auto text-sage" size={56} strokeWidth={1.5} />
          <h1 className="mt-6 font-serif text-3xl sm:text-4xl font-light text-charcoal-900">
            {booking?.status === "confirmed" ? "You're all booked!" : "Deposit received — thank you!"}
          </h1>
          <p className="mt-3 text-charcoal-600 text-lg">
            {booking?.status === "confirmed"
              ? "A confirmation and calendar invite are on their way to your email."
              : "We've received your deposit and your time is held. We'll review and confirm shortly, then email your video call link."}
          </p>

          {booking && (
            <div className="mt-10 rounded-3xl bg-white p-8 border border-sand-200 text-left space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="text-terracotta" strokeWidth={1.5} />
                <span className="text-charcoal-800 font-medium">{formatSlot(booking.slot)}</span>
              </div>
              <div className="flex items-center gap-3">
                <Video className="text-terracotta" strokeWidth={1.5} />
                <span className="text-charcoal-700">{booking.platform}</span>
              </div>
              {booking.status === "confirmed" && booking.manage_token && (
                <Link to={`/call?token=${booking.manage_token}`} data-testid="confirm-join-call" className="inline-flex rounded-full bg-sage px-6 py-2.5 text-white font-medium hover:bg-sage-hover">
                  Join your video call
                </Link>
              )}
            </div>
          )}

          <div className="mt-8 rounded-3xl bg-sage-light p-8 text-left">
            <h2 className="font-serif text-xl text-charcoal-800 mb-2">What to expect</h2>
            <p className="text-charcoal-700 leading-relaxed">
              This is a relaxed, judgement-free conversation. Bring your questions, your worries, and your
              timeline — you'll leave with clarity and a written set of next steps.
            </p>
          </div>

          <Link to="/" className="mt-8 inline-flex rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
            Back home
          </Link>
          <div className="mt-4">
            <a
              data-testid="confirm-whatsapp-cta"
              href="https://wa.me/529993616898?text=Hola!%20I%20just%20booked%20a%20consultation%20with%20Hola%20Movers%20and%20have%20a%20question."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#25D366] text-[#128C4B] px-6 py-2.5 text-sm font-medium hover:bg-[#25D366]/10 transition-colors"
            >
              Chat on WhatsApp
            </a>
          </div>
          {booking?.manage_token && (
            <p className="mt-5 text-sm text-charcoal-500">
              Need to make a change?{" "}
              <Link to={`/booking/manage?token=${booking.manage_token}`} data-testid="confirm-manage-link" className="text-terracotta underline underline-offset-2 font-medium">
                Reschedule or cancel
              </Link>
            </p>
          )}
        </div>
      )}

      {(state === "failed" || state === "error") && (
        <div className="text-center" data-testid="confirm-failed">
          <XCircle className="mx-auto text-destructive" size={52} strokeWidth={1.5} />
          <h1 className="mt-6 font-serif text-2xl text-charcoal-800">We couldn't confirm your payment</h1>
          <p className="mt-3 text-charcoal-600">
            If you completed payment, please check your email — your confirmation may still arrive. Otherwise,
            you can try booking again.
          </p>
          <Link to="/book" className="mt-8 inline-flex rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
            Try again
          </Link>
        </div>
      )}
    </div>
  );
}
